/**
 * Models: bringing one in, and making it stand on a tile.
 *
 * A model made anywhere else arrives at whatever size and origin its author used.
 * The pack the game ships with is built to one metre and a half per unit with its
 * feet at zero, and anything that is not has to be told what to do about it. That
 * is what this panel is: a file, then a size, a nudge and a turn until it sits
 * where it should, and the tags saying where it may be used.
 *
 * The fitting is done against the world rather than against a preview box: the
 * model is put down where the character stands, and moved as the numbers change,
 * so what is being judged is the thing itself next to everything else.
 */

import type { ModelWire } from '@inverse/protocol';
import { useEffect, useRef, useState } from 'react';
import type { Wanted } from './find';
import type { AdminCommand } from '../Admin';
import { Explain } from '../Explain';

const TAGS = ['scenery', 'object', 'item', 'character'] as const;

const TAG_ABOUT: Readonly<Record<(typeof TAGS)[number], string>> = {
  scenery: 'Trees, rocks, crates: anything standing in the world.',
  object: 'Doors, chests and the like, which have a menu and a state.',
  item: 'Held, worn or lying on the ground.',
  character: 'Drawn as somebody. Static: it will stand rather than walk.',
};

export function EditorModels({
  models,
  users,
  wanted,
  onCommand,
  onUpload,
  onFit,
}: {
  models: readonly ModelWire[];
  /**
   * What is drawn as each model, by name.
   *
   * Asked for rather than worked out here: the client knows every definition,
   * but the world knows which of them are actually written down, and a model
   * manager that guessed would be a manager somebody stopped trusting.
   */
  users: Readonly<Record<string, { scenery: string[]; items: string[]; characters: string[] }>>;
  /** A model somewhere else asked for, such as the search across everything. */
  wanted: Wanted | null;
  onCommand: AdminCommand;
  /** Sends the file itself, which does not go over the game connection. */
  onUpload: (upload: {
    name: string;
    title: string;
    contents: string;
    binary: boolean;
    tags: string[];
  }) => Promise<string | null>;
  /** Puts the model down in the world at the given fitting, so it can be judged. */
  onFit: (model: ModelWire | null) => void;
}): JSX.Element {
  const [chosen, setChosen] = useState<string | null>(null);
  /** A model asked for from outside, taken once so a later press stands. */
  const [took, setTook] = useState(0);
  if (wanted !== null && wanted.at !== took) {
    setTook(wanted.at);
    setChosen(wanted.id);
  }
  /** Whether taking one out has been refused once for being in use. */
  const [warned, setWarned] = useState(false);
  const [saying, setSaying] = useState('');
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);

  const held = models.find((model) => model.name === chosen) ?? null;

  /**
   * Everything drawn as the model being looked at.
   *
   * One list rather than three, because what somebody about to replace or
   * remove a model wants is the size of the job rather than a breakdown of it.
   */
  const mine = held
    ? [
        ...(users[held.name]?.scenery ?? []),
        ...(users[held.name]?.items ?? []),
        ...(users[held.name]?.characters ?? []),
      ]
    : [];

  // Asked for once when the panel opens, and again after a swap.
  useEffect(() => {
    onCommand('listModelUsers');
  }, [onCommand]);

  const change = (patch: Partial<ModelWire>): void => {
    if (!held) return;

    const next = { ...held, ...patch };
    onFit(next);
    onCommand(
      'setModel',
      {
        scale: next.scale,
        offsetX: next.offsetX,
        offsetY: next.offsetY,
        offsetZ: next.offsetZ,
        turns: next.turns,
        tiles: next.tiles,
      },
      { model: next.name, tags: next.tags.join(',') },
    );
  };

  const bring = async (): Promise<void> => {
    const picked = file.current?.files?.[0];
    if (!picked) return;

    setBusy(true);
    setSaying('');

    const binary = picked.name.toLowerCase().endsWith('.glb');
    const name = picked.name
      .replace(/\.(gltf|glb)$/i, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 48);

    try {
      const contents = binary ? await asBase64(picked) : await picked.text();

      const problem = await onUpload({
        name,
        title: picked.name.replace(/\.(gltf|glb)$/i, ''),
        contents,
        binary,
        tags: ['scenery'],
      });

      setSaying(problem ?? '');
      if (!problem) setChosen(name);
    } catch (error) {
      setSaying(error instanceof Error ? error.message : 'That file could not be read.');
    } finally {
      setBusy(false);
      if (file.current) file.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={file}
        className="editor-file"
        type="file"
        accept=".gltf,.glb,model/gltf+json,model/gltf-binary"
        onChange={() => void bring()}
      />

      <p className="hint">
        A glTF file, text or binary.
        <Explain>
          Written beside the rest of the content, so it survives an update and is there for
          everybody the moment it lands.
        </Explain>
      </p>

      {busy ? <p className="hint">Sending it over.</p> : null}
      {saying ? <p className="hint warning">{saying}</p> : null}

      <div className="editor-list">
        {models.map((model) => (
          <button
            key={model.name}
            type="button"
            className={model.name === chosen ? 'editor-row on' : 'editor-row'}
            onClick={() => {
              setChosen(model.name);
              onFit(model);
            }}
          >
            <span>{model.title || model.name}</span>
            <span className="editor-row-id">{model.tags.join(', ') || 'untagged'}</span>
          </button>
        ))}
      </div>

      {models.length === 0 ? <p className="hint">Nothing brought in yet.</p> : null}

      {held ? (
        <div className="editor-facts">
          <h3>{held.title || held.name}</h3>
          <p className="hint">
            Put down where you are standing.
            <Explain>
              Change the numbers until it sits right. Every change is kept as it is made.
            </Explain>
          </p>

          <Number
            label="Size"
            value={held.scale}
            step={0.05}
            onChange={(scale) => change({ scale })}
          />
          <Number
            label="Across"
            value={held.offsetX}
            step={0.05}
            onChange={(offsetX) => change({ offsetX })}
          />
          <Number
            label="Up"
            value={held.offsetY}
            step={0.05}
            onChange={(offsetY) => change({ offsetY })}
          />
          <Number
            label="Along"
            value={held.offsetZ}
            step={0.05}
            onChange={(offsetZ) => change({ offsetZ })}
          />

          <div className="editor-brushes">
            <button
              type="button"
              className="editor-tab"
              onClick={() => change({ turns: (held.turns + 1) % 4 })}
            >
              Turn it ({held.turns})
            </button>
            <button
              type="button"
              className="editor-tab"
              onClick={() => change({ tiles: (held.tiles % 4) + 1 })}
            >
              Covers {held.tiles} {held.tiles === 1 ? 'tile' : 'tiles'}
            </button>
          </div>

          <h4>Used As</h4>
          <div className="editor-brushes">
            {TAGS.map((tag) => (
              <button
                key={tag}
                type="button"
                className={held.tags.includes(tag) ? 'editor-tab on' : 'editor-tab'}
                title={TAG_ABOUT[tag]}
                onClick={() =>
                  change({
                    tags: held.tags.includes(tag)
                      ? held.tags.filter((entry) => entry !== tag)
                      : [...held.tags, tag],
                  })
                }
              >
                {tag}
              </button>
            ))}
          </div>

          {/* What is actually drawn as this, so nothing is taken away from under
              a hundred trees by mistake. */}
          <div className="editor-section">
            <h4>Used By</h4>

            {mine.length === 0 ? (
              <p className="hint">Nothing is drawn as this yet.</p>
            ) : (
              <>
                <p className="hint">
                  {mine.length} {mine.length === 1 ? 'thing' : 'things'}, including{' '}
                  {mine.slice(0, 4).join(', ')}
                  {mine.length > 4 ? ' and more' : ''}.
                </p>

                {/* The tedious half of replacing a model is finding everything
                    that used the old one, and the tools already know. */}
                <label className="editor-field">
                  <span>Draw Them As</span>
                  <select
                    value=""
                    onChange={(event) => {
                      const wanted = event.target.value;
                      if (!wanted) return;

                      onCommand('swapModel', {}, { from: held.name, to: wanted });
                      window.setTimeout(() => onCommand('listModelUsers'), 400);
                    }}
                  >
                    <option value="">Choose a model</option>
                    {[...models]
                      .filter((one) => one.name !== held.name)
                      .sort((a, b) => a.title.localeCompare(b.title))
                      .map((one) => (
                        <option key={one.name} value={one.name}>
                          {one.title}
                        </option>
                      ))}
                  </select>
                </label>
              </>
            )}
          </div>

          <button
            type="button"
            className="wide"
            onClick={() => {
              onFit(null);
              setChosen(null);
            }}
          >
            Put it away
          </button>

          <button
            type="button"
            className="wide danger"
            title={
              mine.length > 0
                ? 'Draw them as something else first, or press twice to take it anyway'
                : 'Nothing is drawn as this'
            }
            onClick={() => {
              // In use, the world refuses the first press and says how many.
              // Pressing again means it anyway, which is the only warning a
              // thing this destructive needs and the only one it should have.
              onCommand('removeModel', { anyway: warned ? 1 : 0 }, { model: held.name });

              if (mine.length > 0 && !warned) {
                setWarned(true);
                return;
              }

              onFit(null);
              setChosen(null);
              setWarned(false);
            }}
          >
            {mine.length > 0 && warned ? 'Take it out anyway' : 'Take it out of the world'}
          </button>
        </div>
      ) : null}
    </>
  );
}

/** One number with a pair of buttons, since a slider is no way to fit a model. */
function Number({
  label,
  value,
  step,
  onChange,
}: {
  label: string;
  value: number;
  step: number;
  onChange: (value: number) => void;
}): JSX.Element {
  const round = (held: number): number => Math.round(held * 1000) / 1000;

  return (
    <div className="editor-fact">
      <span>{label}</span>
      <span className="editor-nudge">
        <button type="button" className="editor-tab" onClick={() => onChange(round(value - step))}>
          −
        </button>
        <input
          type="text"
          value={String(value)}
          onChange={(event) => {
            const held = globalThis.Number(event.target.value);
            if (globalThis.Number.isFinite(held)) onChange(held);
          }}
        />
        <button type="button" className="editor-tab" onClick={() => onChange(round(value + step))}>
          +
        </button>
      </span>
    </div>
  );
}

/** A binary file as the text the upload carries. */
async function asBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}
